<template>
  <div class="space-y-8">
    <div>
      <h1 class="text-base/7 font-semibold text-gray-900 dark:text-white">
        {{ isEdit ? 'Edit metrics connection' : 'New metrics connection' }}
      </h1>
      <p class="mt-1 max-w-xl text-sm text-gray-500 dark:text-gray-400">
        An OpenTelemetry (OTLP/HTTP) endpoint, for example OpenObserve, Grafana Cloud or your own
        OTel Collector. Runners that send their metrics here push them directly from the VM.
      </p>
    </div>

    <p v-if="loading" class="text-sm text-gray-500 dark:text-gray-400">Loading connection…</p>
    <p v-else-if="loadError" class="text-sm text-red-600 dark:text-red-400" role="alert">
      {{ loadError }}
    </p>
    <template v-else>
      <div
        class="flex max-w-xl items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
      >
        <ExclamationTriangleIcon class="mt-0.5 size-5 shrink-0" aria-hidden="true" />
        <p>
          The endpoint and headers are stored on each runner VM that uses this connection, where CI
          jobs can read them. Use credentials that can only write metrics.
        </p>
      </div>

      <form class="max-w-xl space-y-6" novalidate @submit.prevent="onSubmit">
        <div>
          <label for="connection-name" :class="labelClass">Name</label>
          <input
            id="connection-name"
            v-model="form.name"
            type="text"
            :class="inputClass(!!errors.name)"
            :disabled="!canChange"
            placeholder="Grafana Cloud"
            data-1p-ignore
          />
          <p v-if="errors.name" :class="errorClass">{{ errors.name }}</p>
        </div>

        <div>
          <label for="connection-endpoint" :class="labelClass">OTLP endpoint</label>
          <input
            id="connection-endpoint"
            v-model.trim="form.endpoint"
            type="url"
            :class="inputClass(!!errors.endpoint)"
            :disabled="!canChange"
            placeholder="https://api.openobserve.ai/api/your-org"
            aria-describedby="connection-endpoint-desc"
          />
          <p v-if="errors.endpoint" :class="errorClass">{{ errors.endpoint }}</p>
          <p v-else id="connection-endpoint-desc" :class="hintClass">
            The base URL, like <code>OTEL_EXPORTER_OTLP_ENDPOINT</code>. The collector sends to
            <code>/v1/metrics</code> below it. HTTPS only.
          </p>
        </div>

        <fieldset>
          <legend :class="labelClass">Headers</legend>
          <p :class="hintClass">
            Usually <code>Authorization</code>. Values are stored encrypted and never shown again.
          </p>
          <div
            v-for="(header, index) in form.headers"
            :key="header.key"
            class="mt-3 flex gap-2"
            data-testid="connection-header"
          >
            <input
              v-model.trim="header.name"
              type="text"
              :class="inputClass(false)"
              :disabled="!canChange"
              :aria-label="`Header ${index + 1} name`"
              placeholder="Authorization"
              data-1p-ignore
            />
            <input
              v-model="header.value"
              type="password"
              :class="inputClass(false)"
              :disabled="!canChange"
              :aria-label="`Header ${index + 1} value`"
              :placeholder="header.storedName ? 'Unchanged' : 'Bearer …'"
              autocomplete="new-password"
              data-1p-ignore
            />
            <button
              v-if="canChange"
              type="button"
              class="mt-2 shrink-0 rounded-md px-2 text-sm text-gray-500 hover:text-red-600 dark:text-gray-400"
              :aria-label="`Remove header ${index + 1}`"
              @click="form.headers.splice(index, 1)"
            >
              <XMarkIcon class="size-5" aria-hidden="true" />
            </button>
          </div>
          <p v-if="errors.headers" :class="errorClass">{{ errors.headers }}</p>
          <button
            v-if="canChange && form.headers.length < MAX_HEADERS"
            type="button"
            class="mt-3 text-sm font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
            data-testid="add-header"
            @click="addHeader()"
          >
            Add header
          </button>
        </fieldset>

        <div
          v-if="usedBy.length > 0 || externalRunners.length > 0"
          class="rounded-md bg-yellow-50 p-4 dark:bg-yellow-500/10"
          data-testid="connection-runners-warning"
        >
          <p v-if="usedBy.length > 0" class="text-sm text-yellow-800 dark:text-yellow-200">
            Used by {{ usedBy.map((r) => r.name).join(', ') }}. Changing the endpoint or headers
            recreates the VMs of the active ones.
          </p>
          <p
            v-if="externalRunners.length > 0"
            class="text-sm text-yellow-800 dark:text-yellow-200"
            data-testid="connection-external-warning"
          >
            Used by the external runners {{ externalRunners.map((r) => r.name).join(', ') }}. They
            keep the previous endpoint and headers until you run a new install command on each
            server.
          </p>
        </div>

        <div class="flex items-center gap-3">
          <button
            type="button"
            class="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:inset-ring-white/5 dark:hover:bg-white/20"
            @click="router.push({ name: 'Metrics' })"
          >
            Cancel
          </button>
          <button
            v-if="canChange"
            type="submit"
            class="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="saving"
            data-testid="save-connection"
          >
            {{ saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create connection' }}
          </button>
        </div>
        <p v-if="serverError" class="text-sm text-red-600 dark:text-red-400" role="alert">
          {{ serverError }}
        </p>
      </form>
    </template>

    <RecreateRunnersDialog
      :runners="recreate"
      message="This change recreates the VMs of the active runners that use this connection."
      confirm-label="Save and recreate"
      :busy="saving"
      @confirm="save(true)"
      @cancel="recreate = null"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/vue/20/solid';
import RecreateRunnersDialog from '@/components/RecreateRunnersDialog.vue';
import { asProblem, conflictRunners, type CacheProblem } from '@/api/caches';
import {
  createMetricsConnection,
  getMetricsConnection,
  updateMetricsConnection,
  type MetricsConnectionRequest,
  type ExternalMetricsRunner,
  type MetricsRunner,
} from '@/api/metrics-connections';
import { useMonitoringEntitlement } from '@/lib/monitoring-entitlement';
import { useToastStore } from '@/stores/toast';

// Mirrors the backend limits.
const MAX_HEADERS = 8;
const MAX_HEADER_BYTES = 4096;
const RESERVED = [
  'content-type',
  'content-encoding',
  'content-length',
  'host',
  'transfer-encoding',
  'connection',
  'user-agent',
];

interface HeaderRow {
  key: number;
  name: string;
  value: string;
  /** Name of the stored header this row was loaded from; its value is kept when left empty. */
  storedName: string | null;
}

const route = useRoute();
const router = useRouter();
const toast = useToastStore();
const { canChange } = useMonitoringEntitlement();

const connectionId = computed(() => (route.params.id ? String(route.params.id) : ''));
const isEdit = computed(() => connectionId.value !== '');

const form = reactive({ name: '', endpoint: '', headers: [] as HeaderRow[] });
const errors = reactive<{ name?: string; endpoint?: string; headers?: string }>({});
const usedBy = ref<MetricsRunner[]>([]);
const externalRunners = ref<ExternalMetricsRunner[]>([]);
const loading = ref(false);
const loadError = ref('');
const saving = ref(false);
const serverError = ref('');
const recreate = ref<MetricsRunner[] | null>(null);
let nextKey = 0;

const labelClass = 'block text-sm/6 font-medium text-gray-900 dark:text-white';
const hintClass = 'mt-2 text-sm text-gray-500 dark:text-gray-400';
const errorClass = 'mt-2 text-sm text-red-600 dark:text-red-400';

function inputClass(invalid: boolean) {
  const base =
    'mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base outline-1 -outline-offset-1 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 sm:text-sm/6 dark:bg-white/5 dark:disabled:bg-white/10';
  return invalid
    ? `${base} text-red-900 outline-red-300 focus:outline-red-600 dark:text-red-400 dark:outline-red-500/50`
    : `${base} text-gray-900 outline-gray-300 focus:outline-indigo-600 dark:text-white dark:outline-white/10 dark:placeholder:text-gray-500`;
}

function addHeader(name = '', storedName: string | null = null) {
  form.headers.push({ key: nextKey++, name, value: '', storedName });
}

function nameError(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return 'Name is required.';
  if (trimmed.length > 100) return 'Use at most 100 characters.';
  return undefined;
}

function endpointError(value: string): string | undefined {
  if (!value) return 'Endpoint is required.';
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return 'Enter a URL such as https://otlp.example.com.';
  }
  if (url.protocol !== 'https:') return 'Use an https:// URL.';
  if (url.username || url.password || url.search || url.hash || value.length > 512)
    return 'Leave out credentials, query and fragment; put credentials in a header.';
  if (url.pathname.replace(/\/+$/, '').endsWith('/v1/metrics'))
    return 'Enter the base URL without /v1/metrics.';
  return undefined;
}

function headersError(): string | undefined {
  const names = form.headers.map((h) => h.name.toLowerCase());
  if (form.headers.length > MAX_HEADERS) return `At most ${MAX_HEADERS} headers.`;
  for (const header of form.headers) {
    if (!/^[!#$%&'*+.^_`|~0-9A-Za-z-]{1,128}$/.test(header.name))
      return 'Enter header names as HTTP field names of up to 128 characters, such as X-Api-Key.';
    if (RESERVED.includes(header.name.toLowerCase()))
      return `${header.name} is set by the collector and cannot be changed.`;
    const kept = header.storedName?.toLowerCase() === header.name.toLowerCase();
    if (!header.value && !kept) return `Enter a value for ${header.name}.`;
    if (header.value && !/^[\x20-\x21\x23-\x5B\x5D-\x7E]+$/.test(header.value))
      return 'Header values may contain printable ASCII characters except " and \\.';
    if (header.value && header.value !== header.value.trim())
      return 'Header values must not start or end with a space.';
  }
  if (new Set(names).size !== names.length) return 'Each header name may appear only once.';
  const size = form.headers.reduce((sum, h) => sum + h.name.length + h.value.length, 0);
  if (size > MAX_HEADER_BYTES) return `Headers may have at most ${MAX_HEADER_BYTES} characters.`;
  return undefined;
}

function validate(): boolean {
  errors.name = nameError(form.name);
  errors.endpoint = endpointError(form.endpoint);
  errors.headers = headersError();
  return !errors.name && !errors.endpoint && !errors.headers;
}

function payload(): MetricsConnectionRequest {
  return {
    name: form.name.trim(),
    endpoint: form.endpoint,
    headers: form.headers.map((h) =>
      h.value ? { name: h.name, value: h.value } : { name: h.name }
    ),
  };
}

function problemMessage(e: CacheProblem, fallback: string) {
  const data = e?.response?.data;
  if (data?.code === 'INVALID_METRICS_CONNECTION' && data.field)
    return `Check the ${data.field === 'headers' ? 'headers' : data.field}.`;
  if (data?.code === 'METRICS_CONNECTION_NAME_TAKEN')
    return 'Another metrics connection already has this name.';
  return data?.detail || e?.message || fallback;
}

async function onSubmit() {
  serverError.value = '';
  if (!validate()) return;
  await save(false);
}

async function save(confirmed: boolean) {
  saving.value = true;
  serverError.value = '';
  try {
    if (isEdit.value) {
      const result = await updateMetricsConnection(connectionId.value, payload(), confirmed);
      const recreated = result.recreatedRunners.map((r) => r.name);
      const reinstall = (result.reinstallRunners ?? []).map((r) => r.name);
      toast.success(
        [
          'Metrics connection saved.',
          recreated.length ? `Recreating ${recreated.join(', ')}.` : '',
          reinstall.length ? `Reinstall needed on ${reinstall.join(', ')}.` : '',
        ]
          .filter(Boolean)
          .join(' ')
      );
    } else {
      await createMetricsConnection(payload());
      toast.success('Metrics connection created.');
    }
    recreate.value = null;
    await router.push({ name: 'Metrics' });
  } catch (error) {
    const runners = conflictRunners(error, 'METRICS_CONNECTION_RECREATE_REQUIRED');
    if (runners && !confirmed) {
      recreate.value = runners;
      return;
    }
    recreate.value = null;
    const busy = conflictRunners(error, 'RUNNER_LOCKED');
    serverError.value = busy?.length
      ? `These runners are busy, try again later: ${busy.map((r) => r.name).join(', ')}.`
      : problemMessage(asProblem(error), 'Failed to save the metrics connection.');
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  if (!isEdit.value) {
    addHeader('Authorization');
    return;
  }
  loading.value = true;
  try {
    const connection = await getMetricsConnection(connectionId.value);
    form.name = connection.name;
    form.endpoint = connection.endpoint;
    connection.headers.forEach((h) => addHeader(h.name, h.name));
    usedBy.value = connection.usedBy;
    externalRunners.value = connection.externalRunners ?? [];
  } catch (error) {
    loadError.value = problemMessage(asProblem(error), 'Failed to load the metrics connection.');
  } finally {
    loading.value = false;
  }
});
</script>
